import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { GROUP_DIMENSIONS, type GroupBy } from './property-grouping';
import { readFiltersParam, writeFiltersParam } from '@/shared/hooks/use-url-filters';

/** The two list presentations available while a grouping is active. */
export type GroupView = 'list' | 'summary';

/** The query param that carries the active grouping dimension. */
export const GROUP_PARAM = 'group';
/** The query param that carries the list/summary presentation. */
export const VIEW_PARAM = 'view';

const VALID_GROUPS: ReadonlySet<string> = new Set<GroupBy>(['none', ...GROUP_DIMENSIONS]);
const VALID_VIEWS: ReadonlySet<string> = new Set<GroupView>(['list', 'summary']);

/**
 * Coerces a raw query value into a valid {@link GroupBy}, defaulting to `none`.
 *
 * @param raw - The raw `?group=` value (or null when absent).
 * @returns A valid grouping dimension.
 */
export function parseGroup(raw: string | null): GroupBy {
  return raw && VALID_GROUPS.has(raw) ? (raw as GroupBy) : 'none';
}

/**
 * Coerces a raw query value into a valid {@link GroupView}, defaulting to
 * `list`.
 *
 * @param raw - The raw `?view=` value (or null when absent).
 * @returns A valid presentation mode.
 */
export function parseView(raw: string | null): GroupView {
  return raw && VALID_VIEWS.has(raw) ? (raw as GroupView) : 'list';
}

/**
 * The public surface of {@link usePropertyGrouping}.
 */
export interface PropertyGroupingState {
  /** The active grouping dimension (`none` when ungrouped). */
  group: GroupBy;
  /** The active presentation (always `list` while ungrouped). */
  view: GroupView;
  /** True when a real grouping dimension is active. */
  isGrouped: boolean;
  /** Sets the grouping dimension (clears `view` back to `list` for `none`). */
  setGroup: (next: GroupBy) => void;
  /** Switches between the list and summary presentations. */
  setView: (next: GroupView) => void;
  /**
   * Applies a group's filter patch to `?f=` AND exits the grouping
   * (`?group`/`?view` removed) in ONE atomic history write. This is the
   * group-funnel / summary-card click path; it must be a single write to avoid
   * the same-tick `setSearchParams` race between the filter and grouping hooks.
   */
  applyGroupFilterAndExit: <T extends Record<string, unknown>>(
    patch: Partial<T>,
    defaults: T,
  ) => void;
  /** Reports whether a group key is currently collapsed. */
  isCollapsed: (key: string) => boolean;
  /** Toggles one group's collapsed state. */
  toggleCollapsed: (key: string) => void;
  /** Collapses every supplied group key. */
  collapseAll: (keys: string[]) => void;
  /** Expands every group (clears the collapsed set). */
  expandAll: () => void;
  /** Reports whether a group is showing all its rows (past ROWS_PER_GROUP). */
  isShowingAll: (key: string) => boolean;
  /** Toggles one group's "show all rows" state. */
  toggleShowAll: (key: string) => void;
}

/**
 * Writes the grouping dimension into a copy of the given params, clearing the
 * view when the dimension is `none` (summary is meaningless ungrouped).
 *
 * @param prev - The current search params.
 * @param next - The dimension to set.
 * @returns The next params to commit.
 */
function withGroupParam(prev: URLSearchParams, next: GroupBy): URLSearchParams {
  const params = new URLSearchParams(prev);
  if (next === 'none') {
    params.delete(GROUP_PARAM);
    params.delete(VIEW_PARAM);
  } else {
    params.set(GROUP_PARAM, next);
  }
  return params;
}

/**
 * Writes the view into a copy of the given params, dropping it for the default
 * `list` so the URL stays clean.
 *
 * @param prev - The current search params.
 * @param next - The view to set.
 * @returns The next params to commit.
 */
function withViewParam(prev: URLSearchParams, next: GroupView): URLSearchParams {
  const params = new URLSearchParams(prev);
  if (next === 'list') params.delete(VIEW_PARAM);
  else params.set(VIEW_PARAM, next);
  return params;
}

/**
 * Composes a group-funnel click into ONE set of next params: it applies the
 * group's filter patch to `?f=` AND drops `?group`/`?view` together, so a
 * single `setSearchParams(prev => next)` write carries both intents.
 *
 * This exists because the filter (`?f=`, owned by `useUrlFilters`) and the
 * grouping (`?group`/`?view`, owned by `usePropertyGrouping`) are two separate
 * hook instances. Writing to both in the same tick does NOT compose —
 * react-router does not queue `setSearchParams`, and the last write rebuilds
 * the URL from the pre-update params, silently clobbering the first. Collapsing
 * both edits into one pure params transform removes the race entirely: the
 * caller commits exactly one history entry that both filters AND exits the
 * grouping.
 *
 * @param prev - The current search params (the grouped view's URL).
 * @param patch - The advanced-filter patch for the clicked group (from
 *   `groupFilterPatch`).
 * @param defaults - The full set of filter keys with their default values.
 * @returns The next params: `?f=` carrying the merged non-default filters, with
 *   `?group`/`?view` removed.
 */
export function applyGroupFilterExitParams<T extends Record<string, unknown>>(
  prev: URLSearchParams,
  patch: Partial<T>,
  defaults: T,
): URLSearchParams {
  const next = new URLSearchParams(prev);
  const merged = { ...readFiltersParam(next, defaults), ...patch } as T;
  writeFiltersParam(next, merged, defaults);
  next.delete(GROUP_PARAM);
  next.delete(VIEW_PARAM);
  return next;
}

/**
 * Toggles one key's membership in a set, returning a new set.
 *
 * @param set - The current set.
 * @param key - The key to toggle.
 * @returns A new set with the key flipped.
 */
function toggleInSet(set: ReadonlySet<string>, key: string): Set<string> {
  const next = new Set(set);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/**
 * Owns the grouping UI state: the dimension and presentation live in the URL
 * (`?group=`, `?view=`) so they survive reloads and are shareable, while the
 * ephemeral collapsed / show-all sets live in component state (they are view
 * scratch, not something worth putting in a shared link).
 *
 * Invalid URL values fall back to `none` / `list` rather than throwing.
 *
 * @returns The grouping state and its mutators.
 */
export function usePropertyGrouping(): PropertyGroupingState {
  const [searchParams, setSearchParams] = useSearchParams();

  const group = useMemo(() => parseGroup(searchParams.get(GROUP_PARAM)), [searchParams]);
  const rawView = useMemo(() => parseView(searchParams.get(VIEW_PARAM)), [searchParams]);
  const isGrouped = group !== 'none';
  // Summary is meaningless without a grouping, so an ungrouped list is always
  // the plain list regardless of a stale `?view=summary` left in the URL.
  const view: GroupView = isGrouped ? rawView : 'list';

  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [showAll, setShowAll] = useState<ReadonlySet<string>>(new Set());

  const setGroup = useCallback(
    (next: GroupBy) => {
      setSearchParams((prev) => withGroupParam(prev, next), { replace: true });
      // A new dimension re-partitions the list, so stale per-group scratch is
      // meaningless against the new keys.
      setCollapsed(new Set());
      setShowAll(new Set());
    },
    [setSearchParams],
  );

  const setView = useCallback(
    (next: GroupView) => {
      setSearchParams((prev) => withViewParam(prev, next), { replace: true });
    },
    [setSearchParams],
  );

  const applyGroupFilterAndExit = useCallback(
    <T extends Record<string, unknown>>(patch: Partial<T>, defaults: T) => {
      setSearchParams(
        (prev) => applyGroupFilterExitParams(prev, patch, defaults),
        { replace: true },
      );
      // The list is no longer partitioned, so stale per-group scratch is moot.
      setCollapsed(new Set());
      setShowAll(new Set());
    },
    [setSearchParams],
  );

  const isCollapsed = useCallback((key: string) => collapsed.has(key), [collapsed]);
  const toggleCollapsed = useCallback((key: string) => setCollapsed((prev) => toggleInSet(prev, key)), []);
  const collapseAll = useCallback((keys: string[]) => setCollapsed(new Set(keys)), []);
  const expandAll = useCallback(() => setCollapsed(new Set()), []);
  const isShowingAll = useCallback((key: string) => showAll.has(key), [showAll]);
  const toggleShowAll = useCallback((key: string) => setShowAll((prev) => toggleInSet(prev, key)), []);

  return {
    group,
    view,
    isGrouped,
    setGroup,
    setView,
    applyGroupFilterAndExit,
    isCollapsed,
    toggleCollapsed,
    collapseAll,
    expandAll,
    isShowingAll,
    toggleShowAll,
  };
}
